"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.resolveInsertPosition = resolveInsertPosition;
exports.nodeTypeRequiresConnection = nodeTypeRequiresConnection;
exports.insertStepFromNodeType = insertStepFromNodeType;
exports.insertPreparedStep = insertPreparedStep;
exports.removeStep = removeStep;
exports.renameStep = renameStep;
exports.updateStepParams = updateStepParams;
exports.updatePreparedStep = updatePreparedStep;
exports.applyStructuralConfig = applyStructuralConfig;
exports.planStepVersionSwitch = planStepVersionSwitch;
exports.switchStepVersion = switchStepVersion;
exports.isProtectedBoundaryStep = isProtectedBoundaryStep;
exports.validateProtectedBoundarySteps = validateProtectedBoundarySteps;
exports.syncConfigSlotValues = syncConfigSlotValues;
exports.syncInputSlotValues = syncInputSlotValues;
/**
 * Workflow editing commands: the structure changes a person in the builder or
 * an AI tool applies to a workflow — insert, remove and rename a
 * step, update its parameters or its structural configuration, switch it to
 * another installed version.
 *
 * They are pure functions over the runner-shaped definition, so the builder
 * applies them to its local workflow document (also offline) and the server
 * applies the very same logic for AI tools and REST commands. Loading and
 * persisting the definition, registry lookups and the insert capability check
 * stay with the caller, which passes their results in. User errors come back
 * as typed failures; the given definition is never mutated.
 */
const control_flow_1 = require("./control-flow");
const slot_1 = require("./slot");
const structural_config_1 = require("./structural-config");
const workflow_dynamic_slots_1 = require("./workflow-dynamic-slots");
const workflow_graph_1 = require("./workflow-graph");
/** Engine step types the graph shows as one generic `service` step. */
const SERVICE_ENGINE_TYPES = new Set([
    'managed_http',
    'external_action',
    'platform_action',
    'managed_code',
    'managed_ai',
]);
const STRUCTURED_OUTPUT_MARKER = '[StreamLain structured-output contract]';
const SHARED_GRAPH_OPS = {
    insertStepIntoEdges: workflow_graph_1.insertStepIntoEdges,
    removeStepFromEdges: workflow_graph_1.removeStepFromEdges,
    wouldInsertLoopInsideLoop: workflow_graph_1.wouldInsertLoopInsideLoop,
    syncBranchPortEdges: workflow_graph_1.syncBranchPortEdges,
    insertBranchPair: workflow_graph_1.insertBranchPair,
    insertLoopStep: workflow_graph_1.insertLoopStep,
    removeBranchPair: workflow_graph_1.removeBranchPair,
    removeLoopWithBody: workflow_graph_1.removeLoopWithBody,
};
// =============================================================================
// Insert
// =============================================================================
/**
 * Resolves where a step goes. With `targetStepId` the edge from the anchor
 * to that target is split, so inserts on parallel routes land on the
 * intended route; an explicit `splitEdgeId` must then be exactly that edge.
 */
function resolveInsertPosition(definition, request) {
    const { afterStepId, outputIndex, targetStepId } = request;
    if (!findStep(definition.steps, afterStepId)) {
        return afterStepNotFound(afterStepId);
    }
    const explicitSplitEdgeId = request.splitEdgeId;
    if (!targetStepId) {
        return {
            ok: true,
            position: { afterStepId, outputIndex, splitEdgeId: explicitSplitEdgeId },
        };
    }
    if (!findStep(definition.steps, targetStepId)) {
        return fail('INVALID_INSERT_POSITION', `targetNode "${targetStepId}" not found in workflow`);
    }
    if (explicitSplitEdgeId) {
        const explicitEdge = definition.edges.find(edge => edge['id'] === explicitSplitEdgeId);
        if (!explicitEdge) {
            return fail('INVALID_INSERT_POSITION', `splitEdge "${explicitSplitEdgeId}" not found in workflow`);
        }
        if ((0, workflow_graph_1.getEdgeSource)(explicitEdge) !== afterStepId ||
            (0, workflow_graph_1.getEdgeOutputIndex)(explicitEdge) !== outputIndex ||
            (0, workflow_graph_1.getEdgeTarget)(explicitEdge) !== targetStepId) {
            return fail('INVALID_INSERT_POSITION', `splitEdge "${explicitSplitEdgeId}" does not connect afterNode "${afterStepId}" to targetNode "${targetStepId}" on outputIndex ${outputIndex}`);
        }
        return {
            ok: true,
            position: { afterStepId, outputIndex, splitEdgeId: explicitSplitEdgeId },
        };
    }
    const targetEdge = definition.edges.find(edge => (0, workflow_graph_1.getEdgeSource)(edge) === afterStepId &&
        (0, workflow_graph_1.getEdgeOutputIndex)(edge) === outputIndex &&
        (0, workflow_graph_1.getEdgeTarget)(edge) === targetStepId);
    if (!targetEdge) {
        return fail('INVALID_INSERT_POSITION', `targetNode "${targetStepId}" is not connected from afterNode "${afterStepId}" on outputIndex ${outputIndex}`);
    }
    const targetEdgeId = targetEdge['id'];
    if (typeof targetEdgeId !== 'string' || targetEdgeId.trim() === '') {
        return fail('INVALID_INSERT_POSITION', `target edge from "${afterStepId}" to "${targetStepId}" has no stable id`);
    }
    return {
        ok: true,
        position: { afterStepId, outputIndex, splitEdgeId: targetEdgeId },
    };
}
/** Whether steps of this node type bind a business connection. */
function nodeTypeRequiresConnection(nodeType) {
    return Array.isArray(nodeType.configSlots)
        ? nodeType.configSlots.some(slot => slot.name === 'connectionId')
        : false;
}
/**
 * Inserts a step of a registered node type the way the AI tools do: branch
 * types become a branch/merge pair, loops a loop step, everything else one
 * step carrying the node type's slots, seeded configuration and wiring.
 */
function insertStepFromNodeType(definition, input, deps = {}) {
    const graph = deps.graph ?? SHARED_GRAPH_OPS;
    const generateId = deps.generateId ?? workflow_graph_1.generateStepId;
    const draft = cloneDraft(definition);
    const afterStep = findStep(draft.steps, input.afterStepId);
    if (!afterStep)
        return afterStepNotFound(input.afterStepId);
    if (input.connectionId && !nodeTypeRequiresConnection(input.nodeTypeDef)) {
        return fail('CONNECTION_NOT_SUPPORTED', 'connectionId is only supported by business service nodes');
    }
    const engineType = input.nodeTypeDef.stepType;
    const anchorId = stepIdOf(afterStep);
    const controlName = () => input.name ?? `${input.nodeTypeSlug}-${generateId('step').slice(-4)}`;
    if (control_flow_1.CONTROL_BRANCH_TYPES.has(engineType)) {
        if (splitEdgeMissing(draft, input.splitEdgeId)) {
            return splitEdgeNotFound(input.splitEdgeId);
        }
        const result = graph.insertBranchPair(draft.steps, draft.edges, anchorId, input.outputIndex, engineType, controlName(), input.splitEdgeId);
        draft.steps = result.steps;
        draft.edges = result.edges;
        return {
            ok: true,
            definition: draft,
            ops: [
                insertOp(draft, result.insertedStepId, result.insertedStepName, input),
            ],
            kind: 'branch',
            insertedStepId: result.insertedStepId,
            insertedStepName: result.insertedStepName,
        };
    }
    if (engineType === 'loop') {
        if (graph.wouldInsertLoopInsideLoop(draft.steps, draft.edges, anchorId, input.outputIndex, input.splitEdgeId)) {
            return loopInsideLoop();
        }
        if (splitEdgeMissing(draft, input.splitEdgeId)) {
            return splitEdgeNotFound(input.splitEdgeId);
        }
        const result = graph.insertLoopStep(draft.steps, draft.edges, anchorId, input.outputIndex, {
            ...input.nodeTypeDef.executionTemplate,
            ...buildConfigOverrides(input),
        }, controlName(), input.splitEdgeId);
        draft.steps = result.steps;
        draft.edges = result.edges;
        return {
            ok: true,
            definition: draft,
            ops: [
                insertOp(draft, result.insertedStepId, result.insertedStepName, input),
            ],
            kind: 'loop',
            insertedStepId: result.insertedStepId,
            insertedStepName: result.insertedStepName,
        };
    }
    const newId = generateId('step');
    const genericName = `${input.nodeTypeSlug}-${newId.slice(-4)}`;
    const graphType = SERVICE_ENGINE_TYPES.has(engineType)
        ? 'service'
        : engineType;
    const newStep = {
        id: newId,
        name: input.name ?? genericName,
        type: graphType,
        config: {
            ...input.nodeTypeDef.executionTemplate,
            ...buildConfigOverrides(input),
            type: graphType,
            nodeTypeSlug: input.nodeTypeSlug,
        },
        position: { x: 0, y: 0 },
        inputMappings: [],
    };
    copyNodeTypeSlots(input.nodeTypeDef, newStep);
    syncConfigSlotValues(newStep, configOf(newStep));
    syncDynamicInputSlots(newStep, configOf(newStep));
    const inputMappings = input.inputMappings ?? inferSingleLinearInputMapping(afterStep, newStep);
    if (inputMappings.length > 0) {
        const mappingError = validateInputMappings(draft, newStep, inputMappings);
        if (mappingError)
            return fail('INVALID_INPUT_MAPPING', mappingError);
        newStep['inputMappings'] = toRuntimeInputMappings(draft.steps, newStep, inputMappings);
    }
    const sourceUpdate = prepareAIOutputForSanitizer(afterStep, input.nodeTypeSlug);
    draft.steps = [...draft.steps, newStep];
    if (splitEdgeMissing(draft, input.splitEdgeId)) {
        return splitEdgeNotFound(input.splitEdgeId);
    }
    draft.edges = graph.insertStepIntoEdges(draft.edges, anchorId, newId, input.outputIndex, input.splitEdgeId);
    const contractUpdates = refreshAIOutputContracts(draft);
    return {
        ok: true,
        definition: draft,
        ops: [
            ...(sourceUpdate
                ? [
                    {
                        op: 'update',
                        nodeName: String(afterStep['name'] ?? anchorId),
                        before: sourceUpdate.before,
                        after: sourceUpdate.after,
                    },
                ]
                : []),
            ...contractUpdates,
            {
                op: 'insert',
                nodeName: genericName,
                nodeType: input.nodeTypeSlug,
                after: newStep,
            },
        ],
        kind: 'step',
        insertedStepId: newId,
        insertedStepName: genericName,
    };
}
/**
 * Inserts a step the builder prepared itself (slots and seeded values are
 * already on it). Branches and loops are generated here because their
 * paired steps and edges belong to the graph shape.
 */
function insertPreparedStep(definition, input, deps = {}) {
    const graph = deps.graph ?? SHARED_GRAPH_OPS;
    const draft = cloneDraft(definition);
    if (!findStep(draft.steps, input.afterStepId)) {
        return afterStepNotFound(input.afterStepId);
    }
    if (input.stepType === 'branch' || control_flow_1.CONTROL_BRANCH_TYPES.has(input.stepType)) {
        const branchType = control_flow_1.CONTROL_BRANCH_TYPES.has(input.stepType)
            ? input.stepType
            : (input.branchType ?? 'branch_or');
        if (splitEdgeMissing(draft, input.splitEdgeId)) {
            return splitEdgeNotFound(input.splitEdgeId);
        }
        const result = graph.insertBranchPair(draft.steps, draft.edges, input.afterStepId, input.outputIndex, branchType, input.stepName, input.splitEdgeId);
        draft.steps = result.steps;
        draft.edges = result.edges;
        return {
            ok: true,
            definition: draft,
            kind: 'branch',
            insertedStepId: result.insertedStepId,
            insertedStepName: result.insertedStepName,
            mergeId: result.mergeId,
        };
    }
    if (input.stepType === 'loop') {
        if (graph.wouldInsertLoopInsideLoop(draft.steps, draft.edges, input.afterStepId, input.outputIndex, input.splitEdgeId)) {
            return loopInsideLoop();
        }
        if (splitEdgeMissing(draft, input.splitEdgeId)) {
            return splitEdgeNotFound(input.splitEdgeId);
        }
        const result = graph.insertLoopStep(draft.steps, draft.edges, input.afterStepId, input.outputIndex, input.stepConfig, input.stepName, input.splitEdgeId);
        draft.steps = result.steps;
        draft.edges = result.edges;
        return {
            ok: true,
            definition: draft,
            kind: 'loop',
            insertedStepId: result.insertedStepId,
            insertedStepName: result.insertedStepName,
        };
    }
    if (!input.preparedStep) {
        return fail('INVALID_STEP', 'preparedStep is required for generic node insertion');
    }
    const newStep = structuredClone(input.preparedStep);
    const newStepId = stepIdOf(newStep);
    if (!newStepId)
        return fail('INVALID_STEP', 'preparedStep.id is required');
    draft.steps = [...draft.steps, newStep];
    if (splitEdgeMissing(draft, input.splitEdgeId)) {
        return splitEdgeNotFound(input.splitEdgeId);
    }
    draft.edges = graph.insertStepIntoEdges(draft.edges, input.afterStepId, newStepId, input.outputIndex, input.splitEdgeId);
    return {
        ok: true,
        definition: draft,
        kind: 'step',
        insertedStepId: newStepId,
        insertedStepName: stepNameOf(newStep) || newStepId,
    };
}
// =============================================================================
// Remove, rename
// =============================================================================
/**
 * Removes a step. With `cascade` a branch or merge takes its pair along and
 * a loop its body; without it only the step itself goes and its neighbours
 * are reconnected. Start and End are never removed.
 */
function removeStep(definition, input, deps = {}) {
    const graph = deps.graph ?? SHARED_GRAPH_OPS;
    const draft = cloneDraft(definition);
    const before = findStep(draft.steps, input.stepId);
    if (!before)
        return stepNotFound(input.stepId);
    if (isProtectedBoundaryStep(before))
        return protectedBoundaryStep();
    const type = String(before['type'] ?? '');
    let removedStepIds = [input.stepId];
    if (input.cascade && (control_flow_1.CONTROL_BRANCH_TYPES.has(type) || type === 'merge')) {
        const result = graph.removeBranchPair(draft.steps, draft.edges, input.stepId);
        draft.steps = result.steps;
        draft.edges = result.edges;
        removedStepIds = result.removedStepIds;
    }
    else if (input.cascade && type === 'loop') {
        const result = graph.removeLoopWithBody(draft.steps, draft.edges, input.stepId);
        draft.steps = result.steps;
        draft.edges = result.edges;
        removedStepIds = result.removedStepIds;
    }
    else {
        draft.steps = draft.steps.filter(s => stepIdOf(s) !== input.stepId);
        draft.edges = graph.removeStepFromEdges(draft.edges, input.stepId);
    }
    return {
        ok: true,
        definition: draft,
        ops: [{ op: 'remove', nodeName: input.stepId, before }],
        removedStepIds,
    };
}
/** Renames a step. References use step ids, so nothing else changes. */
function renameStep(definition, input) {
    const draft = cloneDraft(definition);
    const stepIndex = draft.steps.findIndex(s => stepIdOf(s) === input.stepId);
    if (stepIndex < 0)
        return stepNotFound(input.stepId);
    draft.steps = draft.steps.map((step, index) => index === stepIndex ? { ...step, name: input.newName } : step);
    return {
        ok: true,
        definition: draft,
        ops: [{ op: 'rename', oldName: input.stepId, newName: input.newName }],
    };
}
/**
 * Merges parameters into a step the way the AI tools edit it: legacy
 * parameter aliases are normalised, slots derived from the configuration
 * (dynamic inputs/outputs, config slot values, branch route edges) follow,
 * and AI steps feeding an Output Sanitizer keep returning raw text.
 */
function updateStepParams(definition, input, deps = {}) {
    const graph = deps.graph ?? SHARED_GRAPH_OPS;
    const draft = cloneDraft(definition);
    const step = findStep(draft.steps, input.stepId);
    if (!step)
        return stepNotFound(input.stepId);
    const nodeType = typeOf(step);
    if (input.inputFields !== undefined && nodeType !== 'start') {
        return fail('INVALID_PARAMETERS', 'inputFields is only valid for the Start node (nodeId: "start")');
    }
    if (input.outputFields !== undefined && nodeType !== 'end') {
        return fail('INVALID_PARAMETERS', 'outputFields is only valid for the End node (nodeId: "end")');
    }
    const ops = [];
    if (input.inputFields !== undefined) {
        const beforeSchema = draft.inputSchema ?? { fields: [] };
        draft.inputSchema = { ...draft.inputSchema, fields: input.inputFields };
        ops.push({
            op: 'update',
            nodeName: input.stepId,
            before: beforeSchema,
            after: draft.inputSchema,
        });
    }
    if (input.outputFields !== undefined) {
        const beforeSchema = draft.outputSchema ?? { fields: [] };
        draft.outputSchema = { ...draft.outputSchema, fields: input.outputFields };
        ops.push({
            op: 'update',
            nodeName: input.stepId,
            before: beforeSchema,
            after: draft.outputSchema,
        });
    }
    const before = { ...step['config'] };
    const normalizedParameters = normalizeNodeParameters(step, input.parameters);
    const after = { ...before, ...normalizedParameters };
    keepAIOutputRawBeforeSanitizer(draft, step, after);
    // The sanitizer contract may rewrite these two keys; project the result
    // onto the config slots like any other changed parameter.
    for (const key of ['responseFormat', 'systemPrompt']) {
        if (normalizedParameters[key] !== undefined || after[key] !== before[key]) {
            normalizedParameters[key] = after[key];
        }
    }
    if (nodeType === 'http' &&
        ('url' in after || !input.isHttpTarget(after['target']))) {
        return fail('HTTP_STEP_RECONFIGURATION_REQUIRED', 'HTTP_STEP_RECONFIGURATION_REQUIRED');
    }
    syncDynamicInputSlots(step, after);
    syncDynamicOutputSlots(step, after);
    step['config'] = after;
    if (input.inputMappings !== undefined) {
        const mappingError = validateInputMappings(draft, step, input.inputMappings);
        if (mappingError)
            return fail('INVALID_INPUT_MAPPING', mappingError);
        const beforeMappings = step['inputMappings'] ?? [];
        const runtimeMappings = toRuntimeInputMappings(draft.steps, step, input.inputMappings);
        step['inputMappings'] = runtimeMappings;
        ops.push({
            op: 'update',
            nodeName: input.stepId,
            before: { inputMappings: beforeMappings },
            after: { inputMappings: runtimeMappings },
        });
    }
    if (control_flow_1.CONTROL_BRANCH_TYPES.has(String(step['type'] ?? ''))) {
        const routed = syncRouteEdges(graph, stepIdOf(step), before, after, draft);
        if (!routed.ok)
            return routed;
        draft.edges = routed.edges;
    }
    syncConfigSlotValues(step, normalizedParameters);
    syncInputSlotValues(step, normalizedParameters);
    ops.push({ op: 'update', nodeName: input.stepId, before, after });
    ops.push(...refreshAIOutputContracts(draft));
    return { ok: true, definition: draft, ops };
}
/**
 * Replaces a step with a version the editor prepared. What the graph derives
 * from the configuration follows: JSON Extract output slots and branch route
 * edges, plus the config slot values the route and field editors read.
 */
function updatePreparedStep(definition, input, deps = {}) {
    const graph = deps.graph ?? SHARED_GRAPH_OPS;
    const draft = cloneDraft(definition);
    const stepIndex = draft.steps.findIndex(s => stepIdOf(s) === input.stepId);
    if (stepIndex < 0)
        return stepNotFound(input.stepId);
    const currentStep = draft.steps[stepIndex];
    const nextStep = structuredClone(input.step);
    if (stepIdOf(nextStep) !== input.stepId) {
        return fail('INVALID_STEP', 'Updated step id must match stepId');
    }
    const nextType = typeOf(nextStep);
    if (nextType === 'json_extract') {
        // JSON Extract outputs are derived from its field configuration and must
        // stay identical across builder and AI edits, so the prepared step gets
        // the same normalisation as `updateStepParams`.
        const normalizedConfig = normalizeNodeParameters(nextStep, nextStep['config'] ?? {});
        syncDynamicOutputSlots(nextStep, normalizedConfig);
        nextStep['config'] = normalizedConfig;
        syncConfigSlotValues(nextStep, normalizedConfig);
    }
    draft.steps = draft.steps.map((step, index) => index === stepIndex ? nextStep : step);
    if (control_flow_1.CONTROL_BRANCH_TYPES.has(nextType)) {
        const nextConfig = nextStep['config'] ?? {};
        const routed = syncRouteEdges(graph, input.stepId, currentStep['config'] ?? {}, nextConfig, draft);
        if (!routed.ok)
            return routed;
        draft.edges = routed.edges;
        // The route editor reads the routes from the config slots.
        syncConfigSlotValues(nextStep, nextConfig);
    }
    return { ok: true, definition: draft };
}
/**
 * Writes the configuration values that reshape the graph (branch routes,
 * JSON Extract fields; see `structuralConfigKeys`). Other keys are refused:
 * they are plain field values the editor writes straight into the document.
 */
function applyStructuralConfig(definition, input, deps = {}) {
    const step = findStep(definition.steps, input.stepId);
    if (!step)
        return stepNotFound(input.stepId);
    const allowed = (0, structural_config_1.structuralConfigKeys)(String(step['type'] ?? ''));
    const foreign = Object.keys(input.config).filter(key => !allowed.includes(key));
    if (foreign.length > 0) {
        return fail('STEP_CONFIG_NOT_STRUCTURAL', `Only ${allowed.join(', ') || 'no keys'} of this step reshape the graph`);
    }
    return updatePreparedStep(definition, {
        stepId: input.stepId,
        step: { ...step, config: { ...configOf(step), ...input.config } },
    }, deps);
}
// =============================================================================
// Installed version switch
// =============================================================================
/**
 * Rebuilds a step record for another installed version of the same step
 * template. Slot values the user already set are carried over by slot id,
 * then by slot name; slots the target version no longer declares are
 * dropped, new slots start empty. Materialized dynamic inputs survive when
 * the target version still accepts dynamic inputs.
 */
function planStepVersionSwitch(step, current, target) {
    if (current.definition.id !== target.definition.id) {
        return fail('MARKETPLACE_STEP_VERSION_TEMPLATE_MISMATCH', 'A node can only switch between versions of the same step');
    }
    if (!target.active) {
        return fail('MARKETPLACE_STEP_VERSION_NOT_INSTALLED', 'The target step version is not installed in this workspace');
    }
    const definition = target.definition;
    const previousInputs = readSlots(step['inputSlots']);
    const inputSlots = carrySlotValues(definition.inputSlots, previousInputs);
    if (definition.dynamicInputs) {
        const declared = new Set(inputSlots.map(slot => slot.id));
        inputSlots.push(...previousInputs.filter(slot => !declared.has(slot.id)));
    }
    return {
        ok: true,
        step: {
            ...step,
            config: { ...configOf(step), nodeTypeSlug: definition.slug },
            inputSlots,
            configSlots: carrySlotValues(definition.configSlots, readSlots(step['configSlots'])),
            // Catalog-owned outputs follow the version; editable dynamic outputs stay.
            outputSlots: definition.dynamicOutputs
                ? step['outputSlots']
                : definition.outputSlots,
        },
    };
}
/** Moves one step to another installed version of the same step template. */
function switchStepVersion(definition, input, deps = {}) {
    const step = findStep(definition.steps, input.stepId);
    if (!step)
        return stepNotFound(input.stepId);
    const planned = planStepVersionSwitch(step, input.current, input.target);
    if (!planned.ok)
        return planned;
    return updatePreparedStep(definition, { stepId: input.stepId, step: planned.step }, deps);
}
// =============================================================================
// Shared step rules
// =============================================================================
/** Start and End frame every workflow; no command may remove them. */
function isProtectedBoundaryStep(step) {
    const id = stepIdOf(step).toLowerCase();
    const type = typeof step['type'] === 'string' ? step['type'].toLowerCase() : '';
    return (id === 'start' ||
        id === 'end' ||
        type === 'start' ||
        type === 'end' ||
        type === 'interface-start' ||
        type === 'interface-end');
}
/** The refusal message when `nextSteps` lost a Start or End of `currentSteps`. */
function validateProtectedBoundarySteps(currentSteps, nextSteps) {
    for (const current of currentSteps) {
        if (!isProtectedBoundaryStep(current))
            continue;
        const currentId = stepIdOf(current);
        const next = nextSteps.find(s => stepIdOf(s) === currentId);
        if (!next || !isProtectedBoundaryStep(next)) {
            return PROTECTED_BOUNDARY_MESSAGE;
        }
    }
    return null;
}
/**
 * Projects changed parameters onto the step's config slots, which the panel
 * reads its values from. Mutates `step`.
 */
function syncConfigSlotValues(step, parameters) {
    const slots = step['configSlots'];
    if (!Array.isArray(slots))
        return;
    step['configSlots'] = slots.map(slot => {
        if (!slot || typeof slot !== 'object')
            return slot;
        const slotRecord = slot;
        const name = slotRecord['name'];
        if (typeof name !== 'string' || !(name in parameters))
            return slot;
        return {
            ...slotRecord,
            value: structuredClone((0, slot_1.configSlotValue)(slotRecord, parameters[name])),
        };
    });
}
/**
 * A parameter named like an input slot is that slot's fixed value (e.g. the
 * subject template of "Send email"). The runner reads mapping, then value,
 * then default; publish accepts a required slot that carries a value. An
 * empty parameter (null or '') removes the value, so the slot counts as
 * unconnected again.
 */
function syncInputSlotValues(step, parameters) {
    const slots = step['inputSlots'];
    if (!Array.isArray(slots))
        return;
    step['inputSlots'] = slots.map(slot => {
        if (!slot || typeof slot !== 'object')
            return slot;
        const slotRecord = slot;
        const name = slotRecord['name'];
        if (typeof name !== 'string' || !(name in parameters))
            return slot;
        if (slotRecord['bindable'] === false)
            return slot;
        const parameter = parameters[name];
        if (parameter === null || parameter === undefined || parameter === '') {
            const cleared = { ...slotRecord };
            delete cleared['value'];
            return cleared;
        }
        return {
            ...slotRecord,
            value: structuredClone((0, slot_1.configSlotValue)(slotRecord, parameter)),
        };
    });
}
// =============================================================================
// Internals
// =============================================================================
const PROTECTED_BOUNDARY_MESSAGE = 'Start and End nodes cannot be removed';
function fail(code, message) {
    return { ok: false, error: { code, message } };
}
function stepNotFound(stepId) {
    return fail('STEP_NOT_FOUND', `Step "${stepId}" not found in workflow`);
}
function afterStepNotFound(stepId) {
    return fail('AFTER_STEP_NOT_FOUND', `afterStepId "${stepId}" does not exist in the workflow`);
}
/**
 * The graph primitives throw for an unknown split edge; checking first keeps
 * that user error a typed failure, with the primitive's wording.
 */
function splitEdgeMissing(draft, splitEdgeId) {
    return !!splitEdgeId && !draft.edges.some(edge => edge['id'] === splitEdgeId);
}
function splitEdgeNotFound(splitEdgeId) {
    return fail('SPLIT_EDGE_NOT_FOUND', `Split edge "${splitEdgeId}" not found`);
}
/**
 * Adds and removes the edges of changed branch routes. A route that still
 * holds steps cannot be removed; the primitive throws, the command refuses.
 */
function syncRouteEdges(graph, branchId, oldConfig, newConfig, draft) {
    try {
        return {
            ok: true,
            edges: graph.syncBranchPortEdges(branchId, oldConfig, newConfig, draft.edges),
        };
    }
    catch (err) {
        if (!(err instanceof workflow_graph_1.BranchRouteNotEmptyError))
            throw err;
        return {
            ok: false,
            error: {
                code: 'BRANCH_ROUTE_NOT_EMPTY',
                message: err.message,
                portId: err.portId,
            },
        };
    }
}
function protectedBoundaryStep() {
    return fail('PROTECTED_BOUNDARY_STEP', PROTECTED_BOUNDARY_MESSAGE);
}
function loopInsideLoop() {
    return fail('LOOP_INSIDE_LOOP', 'Loop steps cannot be inserted inside another loop');
}
/** A copy the command may change freely; the caller's definition stays intact. */
function cloneDraft(definition) {
    return {
        ...definition,
        steps: structuredClone(definition.steps),
        edges: structuredClone(definition.edges),
    };
}
function stepIdOf(step) {
    return step['id'] ?? '';
}
function stepNameOf(step) {
    return step['name'] ?? '';
}
function typeOf(step) {
    return String(step['type'] ?? '').toLowerCase();
}
function configOf(step) {
    return step['config'] ?? {};
}
function findStep(steps, id) {
    return steps.find(s => stepIdOf(s) === id);
}
function insertOp(draft, insertedStepId, insertedStepName, input) {
    return {
        op: 'insert',
        nodeName: insertedStepName,
        nodeType: input.nodeTypeSlug,
        after: findStep(draft.steps, insertedStepId),
    };
}
function buildConfigOverrides(input) {
    const overrides = {};
    if (input.modelId !== undefined)
        overrides['model'] = input.modelId;
    if (input.providerId !== undefined)
        overrides['provider'] = input.providerId;
    if (input.temperature !== undefined) {
        overrides['temperature'] = input.temperature;
    }
    if (input.maxTokens !== undefined)
        overrides['maxTokens'] = input.maxTokens;
    if (input.systemPrompt !== undefined) {
        overrides['systemPrompt'] = input.systemPrompt;
    }
    if (input.runtime !== undefined)
        overrides['runtime'] = input.runtime;
    if (input.userCode !== undefined)
        overrides['code'] = input.userCode;
    if (input.inputVariables !== undefined) {
        overrides['inputVariables'] = Array.isArray(input.inputVariables)
            ? JSON.stringify(input.inputVariables)
            : input.inputVariables;
    }
    if (input.outputVariables !== undefined) {
        overrides['outputVariables'] = Array.isArray(input.outputVariables)
            ? JSON.stringify(input.outputVariables)
            : input.outputVariables;
    }
    if (input.httpTarget !== undefined)
        overrides['target'] = input.httpTarget;
    if (input.httpMethod !== undefined)
        overrides['method'] = input.httpMethod;
    if (input.httpHeaders !== undefined)
        overrides['headers'] = input.httpHeaders;
    if (input.httpBody !== undefined)
        overrides['bodyTemplate'] = input.httpBody;
    if (input.connectionId !== undefined) {
        overrides['connectionId'] = input.connectionId;
    }
    return overrides;
}
function copyNodeTypeSlots(source, target) {
    if (Array.isArray(source.inputSlots)) {
        target['inputSlots'] = structuredClone(source.inputSlots);
    }
    if (Array.isArray(source.configSlots)) {
        target['configSlots'] = structuredClone(source.configSlots);
    }
    if (Array.isArray(source.outputSlots)) {
        target['outputSlots'] = structuredClone(source.outputSlots);
    }
}
function syncDynamicInputSlots(step, config) {
    const dynamicSlots = (0, workflow_dynamic_slots_1.dynamicInputSlotsFromConfig)(config);
    const previousDynamicIds = new Set((0, workflow_dynamic_slots_1.dynamicInputSlotsFromConfig)(configOf(step)).map(slot => String(slot['id'] ?? '')));
    if (dynamicSlots.length === 0 && previousDynamicIds.size === 0)
        return;
    const existingSlots = Array.isArray(step['inputSlots'])
        ? step['inputSlots']
        : [];
    const dynamicIds = new Set(dynamicSlots.map(slot => slot['id']));
    const preserved = existingSlots.filter(slot => {
        if (!slot || typeof slot !== 'object')
            return true;
        const id = String(slot['id'] ?? '');
        return !dynamicIds.has(id) && !previousDynamicIds.has(id);
    });
    step['inputSlots'] = [...preserved, ...dynamicSlots];
}
function syncDynamicOutputSlots(step, config) {
    const dynamicSlots = (0, workflow_dynamic_slots_1.dynamicOutputSlotsFromConfig)(step, config);
    const previousSlots = (0, workflow_dynamic_slots_1.dynamicOutputSlotsFromConfig)(step, configOf(step));
    const previousDynamicIds = new Set(previousSlots.map(slot => String(slot['id'] ?? '')));
    const dynamicAliases = new Set(dynamicSlots.flatMap(slotAliases));
    const previousDynamicAliases = new Set(previousSlots.flatMap(slotAliases));
    if (dynamicSlots.length === 0 && previousDynamicIds.size === 0)
        return;
    const existingSlots = Array.isArray(step['outputSlots'])
        ? step['outputSlots']
        : [];
    const dynamicIds = new Set(dynamicSlots.map(slot => slot['id']));
    const preserved = existingSlots.filter(slot => {
        if (!slot || typeof slot !== 'object')
            return true;
        const id = String(slot['id'] ?? '');
        return (!dynamicIds.has(id) &&
            !previousDynamicIds.has(id) &&
            !dynamicAliases.has(id) &&
            !previousDynamicAliases.has(id));
    });
    step['outputSlots'] = [...preserved, ...dynamicSlots];
}
function slotAliases(slot) {
    return [slot['id'], slot['sourceFieldId']].flatMap(ref => typeof ref === 'string' ? [ref] : []);
}
/**
 * Wires the one unambiguous connection of a linear insert: a source with a
 * single output into a target with a single required bindable input of a
 * compatible type. Anything less clear is left for the author to wire.
 */
function inferSingleLinearInputMapping(sourceStep, targetStep) {
    if (isStartStep(sourceStep))
        return [];
    const sourceSlots = Array.isArray(sourceStep['outputSlots'])
        ? sourceStep['outputSlots'].filter(slot => slot && typeof slot === 'object')
        : [];
    const targetSlots = Array.isArray(targetStep['inputSlots'])
        ? targetStep['inputSlots'].filter(slot => {
            if (!slot || typeof slot !== 'object')
                return false;
            const record = slot;
            return record['required'] === true && record['bindable'] !== false;
        })
        : [];
    if (sourceSlots.length !== 1 || targetSlots.length !== 1)
        return [];
    const source = sourceSlots[0];
    const target = targetSlots[0];
    const sourceRef = source['sourceFieldId'] ?? source['id'] ?? source['name'];
    const targetRef = target['id'] ?? target['name'];
    if (typeof sourceRef !== 'string' || typeof targetRef !== 'string') {
        return [];
    }
    const sourceType = source['type'];
    const targetType = target['type'];
    if (typeof sourceType === 'string' &&
        typeof targetType === 'string' &&
        sourceType !== targetType &&
        targetType !== 'any') {
        return [];
    }
    return [
        {
            sourceNode: stepIdOf(sourceStep),
            sourceField: sourceRef,
            targetKey: targetRef,
        },
    ];
}
function validateInputMappings(definition, targetStep, mappings) {
    const startFieldIds = new Set((definition.inputSchema?.['fields'] ?? []).map(f => f.id));
    for (const m of mappings) {
        const source = findStep(definition.steps, m.sourceNode);
        if (!source) {
            return `inputMapping references unknown sourceNode "${m.sourceNode}"`;
        }
        if (isStartStep(source) && !startFieldIds.has(m.sourceField)) {
            return `inputMapping sourceField "${m.sourceField}" does not exist on Start node (inputSchema.fields)`;
        }
        if (!isStartStep(source)) {
            const outputRefs = outputSlotReferences(source);
            if (outputRefs.size > 0 && !outputRefs.has(m.sourceField)) {
                return `inputMapping sourceField "${m.sourceField}" does not exist on source node "${m.sourceNode}" outputSlots`;
            }
        }
    }
    if (isEndStep(targetStep)) {
        const outputFields = definition.outputSchema?.['fields'] ?? [];
        const fieldIds = new Set(outputFields.map(f => f.id));
        for (const m of mappings) {
            if (!fieldIds.has(m.targetKey)) {
                return `inputMapping targetKey "${m.targetKey}" does not match any End-node outputField id`;
            }
        }
    }
    else {
        const targetInputSlotRefs = inputSlotReferences(targetStep);
        if (targetInputSlotRefs.size > 0) {
            for (const m of mappings) {
                if (!targetInputSlotRefs.has(m.targetKey)) {
                    return `inputMapping targetKey "${m.targetKey}" does not exist on target node inputSlots`;
                }
            }
        }
    }
    return null;
}
function toRuntimeInputMappings(steps, targetStep, mappings) {
    return mappings.map(mapping => {
        const source = findStep(steps, mapping.sourceNode);
        return {
            sourceStepId: source ? stepIdOf(source) : mapping.sourceNode,
            sourceFieldId: source
                ? canonicalSourceFieldId(source, mapping.sourceField)
                : mapping.sourceField,
            targetFieldId: canonicalTargetFieldId(targetStep, mapping.targetKey),
        };
    });
}
function canonicalSourceFieldId(sourceStep, sourceField) {
    if (isStartStep(sourceStep))
        return sourceField;
    const sourceStepId = stepIdOf(sourceStep);
    if (sourceField.startsWith(`field-${sourceStepId}-`)) {
        return sourceField;
    }
    if (typeOf(sourceStep) === 'json_extract') {
        return (slotIdByRef(sourceStep, 'outputSlots', sourceField) ??
            `field-${sourceStepId}-${sourceField}`);
    }
    if (outputSlotNameExists(sourceStep, sourceField)) {
        return sourceField;
    }
    return (slotIdByRef(sourceStep, 'outputSlots', sourceField) ??
        `field-${sourceStepId}-${sourceField}`);
}
function canonicalTargetFieldId(targetStep, targetKey) {
    if (isEndStep(targetStep))
        return targetKey;
    return slotIdByRef(targetStep, 'inputSlots', targetKey) ?? targetKey;
}
/**
 * Maps the parameter spellings AI tools and older editors send onto the
 * canonical runtime configuration keys.
 */
function normalizeNodeParameters(step, parameters) {
    const normalized = { ...parameters };
    const type = typeOf(step);
    if (type === 'ai') {
        const aliases = [
            ['model', 'modelId', '_slAiModel'],
            ['provider', 'providerId', '_slAiProvider'],
            ['systemPrompt', '_slAiSystemPrompt'],
            ['temperature', '_slAiTemperature'],
            ['maxTokens', '_slAiMaxTokens'],
            ['capability', '_slAiCapability'],
        ];
        for (const [canonical, ...legacy] of aliases) {
            if (normalized[canonical] !== undefined)
                continue;
            const alias = legacy.find(key => normalized[key] !== undefined);
            if (alias)
                normalized[canonical] = normalized[alias];
        }
    }
    if (type === 'json_extract' && Array.isArray(normalized['fields'])) {
        normalized['fields'] = normalized['fields'].map(field => {
            if (!field || typeof field !== 'object')
                return field;
            const record = { ...field };
            if (record['outputName'] === undefined &&
                typeof record['name'] === 'string') {
                record['outputName'] = record['name'];
            }
            delete record['name'];
            if (record['type'] === 'string')
                record['type'] = 'text';
            // The JSON transform emits `outputFieldId` as its raw object key.
            // Keep that key equal to the public output name so both the current
            // runner and older DEV warm-pool runners can map it through
            // `outputSlots[].name`. The stable runtime identity remains
            // `outputSlots[].id`, derived separately from the field's immutable
            // `id`; putting the fully-qualified slot id into the transform config
            // makes legacy runners prefix it a second time.
            if (typeof record['outputName'] === 'string' &&
                record['outputName'].trim() !== '') {
                record['outputFieldId'] = record['outputName'].trim();
            }
            return record;
        });
    }
    if (Array.isArray(normalized['inputVariables'])) {
        normalized['inputVariables'] = JSON.stringify(normalized['inputVariables']);
    }
    if (Array.isArray(normalized['outputVariables'])) {
        normalized['outputVariables'] = JSON.stringify(normalized['outputVariables']);
    }
    if (type === 'code' &&
        typeof normalized['userCode'] === 'string' &&
        normalized['code'] === undefined) {
        normalized['code'] = normalized['userCode'];
        delete normalized['userCode'];
    }
    if (type === 'http') {
        const bodyAlias = normalized['bodyTemplate'] ??
            normalized['httpBody'] ??
            normalized['body'];
        if (bodyAlias !== undefined) {
            normalized['bodyTemplate'] = bodyAlias;
            delete normalized['httpBody'];
            delete normalized['body'];
        }
    }
    return normalized;
}
/**
 * Output Sanitizer consumes the AI node's raw text and owns JSON parsing.
 * Keeping the predecessor in `json` response mode makes the runner parse the
 * provider response first, so the declared `content` output slot disappears
 * and the otherwise-valid content mapping resolves to `undefined`.
 */
function prepareAIOutputForSanitizer(sourceStep, insertedNodeTypeSlug) {
    if (insertedNodeTypeSlug !== 'data-output-sanitizer' ||
        typeOf(sourceStep) !== 'ai') {
        return undefined;
    }
    const before = { ...configOf(sourceStep) };
    if (before['responseFormat'] !== 'json')
        return undefined;
    const after = { ...before, responseFormat: 'text' };
    sourceStep['config'] = after;
    syncConfigSlotValues(sourceStep, { responseFormat: 'text' });
    return { before, after };
}
/**
 * An AI step feeding an Output Sanitizer must answer in text; when a JSON
 * Extract reads the sanitizer, the AI prompt also carries the exact keys the
 * extract expects. Writes into `config`.
 */
function keepAIOutputRawBeforeSanitizer(definition, step, config) {
    if (typeOf(step) !== 'ai')
        return;
    const sourceId = stepIdOf(step);
    const sanitizerConsumers = definition.edges.flatMap(edge => {
        if (String(edge['sourceStepId'] ?? edge['source']) !== sourceId) {
            return [];
        }
        const targetId = String(edge['targetStepId'] ?? edge['target'] ?? '');
        const target = findStep(definition.steps, targetId);
        const targetConfig = target?.['config'] && typeof target['config'] === 'object'
            ? target['config']
            : undefined;
        return String(targetConfig?.['nodeTypeSlug'] ?? '') ===
            'data-output-sanitizer' ||
            String(target?.['type'] ?? '').toLowerCase() === 'output_sanitizer'
            ? [target]
            : [];
    });
    if (sanitizerConsumers.length === 0)
        return;
    config['responseFormat'] = 'text';
    const exactKeys = sanitizerConsumers.flatMap(sanitizer => {
        const sanitizerId = stepIdOf(sanitizer);
        return definition.edges.flatMap(edge => {
            if (String(edge['sourceStepId'] ?? edge['source']) !== sanitizerId) {
                return [];
            }
            const targetId = String(edge['targetStepId'] ?? edge['target'] ?? '');
            const target = findStep(definition.steps, targetId);
            if (String(target?.['type'] ?? '').toLowerCase() !== 'json_extract') {
                return [];
            }
            const targetConfig = target?.['config'] && typeof target['config'] === 'object'
                ? target['config']
                : {};
            const fields = Array.isArray(targetConfig['fields'])
                ? targetConfig['fields']
                : [];
            return fields.flatMap(field => {
                if (!field || typeof field !== 'object')
                    return [];
                const outputName = field['outputName'];
                return typeof outputName === 'string' && outputName.trim() !== ''
                    ? [outputName.trim()]
                    : [];
            });
        });
    });
    if (exactKeys.length === 0)
        return;
    const uniqueKeys = [...new Set(exactKeys)];
    const contract = `Return exactly one JSON object with these exact keys: ${uniqueKeys
        .map(key => `"${key}"`)
        .join(', ')}. ` +
        'Use the keys exactly as written and return no prose outside the JSON object.';
    const currentPrompt = typeof config['systemPrompt'] === 'string'
        ? config['systemPrompt'].replace(/\n?\[StreamLain structured-output contract\][\s\S]*$/, '')
        : '';
    config['systemPrompt'] =
        `${currentPrompt.trim()}\n\n${STRUCTURED_OUTPUT_MARKER} ${contract}`.trim();
}
/**
 * Re-applies the sanitizer contract to every AI step after a change that may
 * have added, removed or reshaped its consumers.
 */
function refreshAIOutputContracts(definition) {
    const ops = [];
    for (const step of definition.steps) {
        if (typeOf(step) !== 'ai')
            continue;
        const before = { ...configOf(step) };
        const after = { ...before };
        keepAIOutputRawBeforeSanitizer(definition, step, after);
        if (JSON.stringify(after) === JSON.stringify(before))
            continue;
        step['config'] = after;
        syncConfigSlotValues(step, {
            responseFormat: after['responseFormat'],
            systemPrompt: after['systemPrompt'],
        });
        ops.push({
            op: 'update',
            nodeName: String(step['name'] ?? stepIdOf(step)),
            before,
            after,
        });
    }
    return ops;
}
function isStartStep(step) {
    if (stepIdOf(step) === 'start')
        return true;
    const type = typeOf(step);
    return (type === 'start' ||
        type === 'core.start' ||
        type === 'swr.start' ||
        type.endsWith('.start'));
}
function isEndStep(step) {
    if (stepIdOf(step) === 'end')
        return true;
    const type = typeOf(step);
    return (type === 'end' ||
        type === 'core.end' ||
        type === 'swr.end' ||
        type.endsWith('.end'));
}
/** Every id, name and source field id a mapping may use to name a slot. */
function slotReferences(slots) {
    const refs = new Set();
    if (!Array.isArray(slots))
        return refs;
    for (const slot of slots) {
        if (!slot || typeof slot !== 'object')
            continue;
        const record = slot;
        for (const value of [
            record['id'],
            record['name'],
            record['sourceFieldId'],
        ]) {
            if (typeof value === 'string' && value.length > 0)
                refs.add(value);
        }
    }
    return refs;
}
function outputSlotReferences(step) {
    return slotReferences(step['outputSlots']);
}
function inputSlotReferences(step) {
    const refs = slotReferences(step['inputSlots']);
    for (const dynamicSlot of (0, workflow_dynamic_slots_1.dynamicInputSlotsFromConfig)(configOf(step))) {
        const id = dynamicSlot['id'];
        const name = dynamicSlot['name'];
        if (typeof id === 'string')
            refs.add(id);
        if (typeof name === 'string')
            refs.add(name);
    }
    return refs;
}
function slotIdByRef(step, slotKey, ref) {
    const slots = step[slotKey];
    if (Array.isArray(slots)) {
        for (const slot of slots) {
            if (!slot || typeof slot !== 'object')
                continue;
            const record = slot;
            const id = record['id'];
            const sourceFieldId = record['sourceFieldId'];
            if (typeof id === 'string' &&
                (id === ref ||
                    record['name'] === ref ||
                    (typeof sourceFieldId === 'string' && sourceFieldId === ref))) {
                return id;
            }
        }
    }
    if (slotKey === 'inputSlots') {
        for (const dynamicSlot of (0, workflow_dynamic_slots_1.dynamicInputSlotsFromConfig)(configOf(step))) {
            const id = dynamicSlot['id'];
            if (typeof id === 'string' &&
                (id === ref || dynamicSlot['name'] === ref)) {
                return id;
            }
        }
    }
    return undefined;
}
function outputSlotNameExists(step, ref) {
    const slots = step['outputSlots'];
    if (!Array.isArray(slots))
        return false;
    return slots.some(slot => !!slot &&
        typeof slot === 'object' &&
        slot['name'] === ref);
}
function carrySlotValues(targetSlots, previousSlots) {
    const byId = new Map(previousSlots.map(slot => [slot.id, slot]));
    const byName = new Map(previousSlots.map(slot => [slot.name, slot]));
    return targetSlots.map(slot => {
        const previous = byId.get(slot.id) ?? byName.get(slot.name);
        return previous?.value === undefined
            ? { ...slot }
            : { ...slot, value: previous.value };
    });
}
function readSlots(value) {
    return Array.isArray(value)
        ? value.filter((slot) => !!slot &&
            typeof slot === 'object' &&
            typeof slot.id === 'string')
        : [];
}
